import { z } from "zod";
import { personNamePartSchema } from "./person-name.js";
import { idSchema } from "./text.js";
// Inviti di un'azienda (piano M1a Task 9, D3). Il token non compare mai in una risposta dell'API né in un
// percorso: sta soltanto nel link consegnato alla persona, nel frammento dopo `#` (`/invito#<token>`), che
// il browser non manda al server, e viaggia verso il server soltanto nel corpo di una richiesta POST.
// Token e password restano stringhe qualunque: non arrivano mai a una query come testo (il token si confronta con il
// suo hash, la password la cifra Better Auth). Nomi e identificativi seguono le regole di `text.ts`.
const token = z.string().min(1).max(256);
/**
 * Corpo di `POST /api/invitations`: nome e cognome con cui la persona comparirà nell'azienda, in due campi. `isProtectedOwner`
 * non si accetta mai da HTTP (lo imposta soltanto il servizio aziende).
 */
export const createInvitationBodySchema = z.strictObject({
    email: z.email().max(254),
    firstName: personNamePartSchema,
    lastName: personNamePartSchema,
    roleId: idSchema,
});
export const invitationSchema = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    /** Nome intero derivato da nome e cognome. */
    displayName: z.string(),
    roleId: z.string(),
    isProtectedOwner: z.boolean(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
});
/** Risposta di creazione e reinvio; `manualDelivery` soltanto quando il mailer non ha consegnato. */
export const invitationIssuedResponseSchema = z.object({
    invitation: invitationSchema,
    manualDelivery: z.object({ link: z.url() }).optional(),
});
/** Risposta di `POST /api/invitations/:id/cancel`: l'invito annullato. */
export const cancelInvitationResponseSchema = z.object({ invitation: invitationSchema });
export const invitationPreviewBodySchema = z.strictObject({ token });
/** Anteprima per la pagina di accettazione (M1b): l'email è mascherata. */
export const invitationPreviewSchema = z.object({
    organizationName: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    /** Nome intero derivato da nome e cognome. */
    displayName: z.string(),
    roleName: z.string(),
    expiresAt: z.iso.datetime(),
    identityExists: z.boolean(),
});
/**
 * Corpo di `POST /api/invitations/accept`: `password` serve a creare l'identità (almeno 12 caratteri) o a
 * verificare quella esistente, che non cambia mai. `firstName` e `lastName` valgono soltanto per un'identità
 * nuova e vanno insieme (uno solo è `422` con i due campi); senza, valgono quelli dell'invito.
 */
export const acceptInvitationBodySchema = z
    .strictObject({
    token,
    password: z.string().min(1).max(128).optional(),
    firstName: personNamePartSchema.optional(),
    lastName: personNamePartSchema.optional(),
})
    .superRefine((body, ctx) => {
    if ((body.firstName === undefined) === (body.lastName === undefined))
        return;
    for (const field of ["firstName", "lastName"]) {
        ctx.addIssue({ code: "custom", path: [field], message: "First and last name go together" });
    }
});
export const acceptInvitationResponseSchema = z.object({ membershipId: z.string(), organizationId: z.string() });
//# sourceMappingURL=invitations.js.map